import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  setAuthTokens,
  clearAuthTokens,
  loginUser,
  registerUser,
  resendVerificationEmail,
  logoutUser,
  fetchUserProfile,
  setTokenChangeCallback,
} from "../apiClient";

const ACCESS_TOKEN_KEY = "project-scraper.accessToken";
const REFRESH_TOKEN_KEY = "project-scraper.refreshToken";

export type UserGroup = "GUEST" | "FREE" | "PRO" | "ORG";

export interface AuthUser {
  id: string;
  supabaseUid: string;
  email: string;
  username: string;
  avatarUrl?: string;
  userGroup: UserGroup;
  emailVerified: boolean;
}

export interface AuthContextValue {
  user: AuthUser | null;
  userGroup: UserGroup;
  isLoggedIn: boolean;
  isEmailVerified: boolean;
  isInitializing: boolean;
  isLoading: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  login: (payload: { email: string; password: string }) => Promise<void>;
  register: (payload: { email: string; password: string; username: string }) => Promise<{
    status: string;
    requiresEmailConfirmation: boolean;
  }>;
  resendEmail: (payload: { email: string }) => Promise<void>;
  logout: (options?: { refreshToken?: string }) => Promise<void>;
  refreshProfile: () => Promise<void>;
  setTokens: (tokens: { accessToken?: string | null; refreshToken?: string | null }) => void;
  hasGroup: (group: UserGroup) => boolean;
  hasAnyGroup: (groups: UserGroup[]) => boolean;
  hasAtLeastGroup: (group: UserGroup) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export interface AuthProviderProps {
  children: ReactNode;
}

const GROUP_PRIORITY: Record<UserGroup, number> = {
  GUEST: 0,
  FREE: 1,
  PRO: 2,
  ORG: 3,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [tokens, setTokensState] = useState<{ accessToken: string | null; refreshToken: string | null }>(() => ({
    accessToken: null,
    refreshToken: null,
  }));

  const persistTokens = useCallback(
    (nextTokens: { accessToken?: string | null; refreshToken?: string | null }) => {
      const merged = {
        accessToken: nextTokens.accessToken ?? tokens.accessToken ?? null,
        refreshToken: nextTokens.refreshToken ?? tokens.refreshToken ?? null,
      };

      setTokensState(merged);
      setAuthTokens(merged);

      if (merged.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, merged.accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (merged.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, merged.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    },
    [tokens.accessToken, tokens.refreshToken],
  );

  // Register callback to sync token changes from apiClient (e.g., after auto-refresh)
  useEffect(() => {
    setTokenChangeCallback((newTokens) => {
      console.log('[AuthContext] Token change callback triggered');
      
      // Update React state
      setTokensState({
        accessToken: newTokens.accessToken,
        refreshToken: newTokens.refreshToken,
      });

      // Persist to localStorage
      if (newTokens.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, newTokens.accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (newTokens.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, newTokens.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }

      // If tokens are cleared, also clear user state
      if (!newTokens.accessToken && !newTokens.refreshToken) {
        setUser(null);
      }
    });

    // Cleanup on unmount
    return () => {
      setTokenChangeCallback(null);
    };
  }, []);

  const clearTokensAndState = useCallback(() => {
    setUser(null);
    setTokensState({ accessToken: null, refreshToken: null });
    clearAuthTokens();
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchUserProfile();
      // API returns { user: {...} } format
      if (response?.user) {
        setUser(response.user as AuthUser);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.warn("[AuthContext] Failed to refresh profile", error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (!storedAccessToken || !storedRefreshToken) {
          clearTokensAndState();
          return;
        }

        setAuthTokens({
          accessToken: storedAccessToken,
          refreshToken: storedRefreshToken,
        });
        setTokensState({ accessToken: storedAccessToken, refreshToken: storedRefreshToken });

        await refreshProfile();
      } catch (error) {
        console.warn("[AuthContext] Failed to restore session", error);
        clearTokensAndState();
      } finally {
        setIsInitializing(false);
      }
    })();
  }, [clearTokensAndState, refreshProfile]);

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setIsLoading(true);
      try {
        const result = await loginUser({ email, password });
        persistTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });

        if (result.user) {
          setUser(result.user as AuthUser);
        } else {
          await refreshProfile();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [persistTokens, refreshProfile],
  );

  const register = useCallback(async ({ email, password, username }: { email: string; password: string; username: string }) => {
    setIsLoading(true);
    try {
      await registerUser({ email, password, username });
      return {
        status: "pending_verification",
        requiresEmailConfirmation: true,
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resendEmail = useCallback(async ({ email }: { email: string }) => {
    await resendVerificationEmail({ email });
  }, []);

  const logout = useCallback(
    async (options?: { refreshToken?: string }) => {
      try {
        await logoutUser({ refreshToken: options?.refreshToken });
      } finally {
        clearTokensAndState();
      }
    },
    [clearTokensAndState],
  );

  const setTokens = useCallback(
    (nextTokens: { accessToken?: string | null; refreshToken?: string | null }) => {
      persistTokens(nextTokens);
    },
    [persistTokens],
  );

  const userGroup: UserGroup = user?.userGroup ?? "GUEST";
  const isLoggedIn = Boolean(user);
  const isEmailVerified = Boolean(user?.emailVerified);

  const hasGroup = useCallback((group: UserGroup) => userGroup === group, [userGroup]);

  const hasAnyGroup = useCallback(
    (groups: UserGroup[]) => groups.some((group) => group === userGroup),
    [userGroup],
  );

  const hasAtLeastGroup = useCallback(
    (group: UserGroup) => GROUP_PRIORITY[userGroup] >= GROUP_PRIORITY[group],
    [userGroup],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      userGroup,
      isLoggedIn,
      isEmailVerified,
      isInitializing,
      isLoading,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      login,
      register,
      resendEmail,
      logout,
      refreshProfile,
      setTokens,
      hasGroup,
      hasAnyGroup,
      hasAtLeastGroup,
    }),
    [
      user,
      userGroup,
      isLoggedIn,
      isEmailVerified,
      isInitializing,
      isLoading,
      tokens.accessToken,
      tokens.refreshToken,
      login,
      register,
      resendEmail,
      logout,
      refreshProfile,
      setTokens,
      hasGroup,
      hasAnyGroup,
      hasAtLeastGroup,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
